import { gql } from '@apollo/client';

/**
 * Manually queues a Fakturownia sync. The backend coalesces duplicate
 * requests in a 60 s window — if it returns `enqueued=false` the message
 * explains why and the frontend should not retry (TZ §3.1, §5).
 */
export const TriggerFakturowniaSyncMutation = gql`
  mutation RunFakturowniaSync($period: String) {
    triggerFakturowniaSync(period: $period) {
      enqueued
      message
      lastRun {
        startedAt
        finishedAt
        status
      }
    }
  }
`;

/**
 * Group-scoped Fakturownia sync — TZ §G10. Mirror of
 * `triggerFakturowniaSync` but narrowed to the union of payer emails
 * of one group. Backs the GroupDetail HeaderBar SyncMenu's
 * Fakturownia item; `emailsTargeted` lets the operator see at a
 * glance how many payers were queried.
 */
export const TriggerFakturowniaGroupSyncMutation = gql`
  mutation RunFakturowniaGroupSync($groupId: ID!, $period: String) {
    triggerFakturowniaGroupSync(groupId: $groupId, period: $period) {
      enqueued
      message
      emailsTargeted
      lastRun {
        startedAt
        finishedAt
        status
      }
    }
  }
`;

/** Attach a (student, group?) to an issued invoice (TZ §3.4). */
export const MatchFakturowniaInvoiceMutation = gql`
  mutation MatchFakturowniaInvoice(
    $invoiceId: ID!
    $studentId: ID!
    $groupId: ID
  ) {
    matchFakturowniaInvoice(
      invoiceId: $invoiceId
      studentId: $studentId
      groupId: $groupId
    ) {
      success
      errors
      invoice {
        id
        matchStatus
        groupMatchSource
        linkedAt
        isCorrection
        isAdvance
        isFinal
        isEstimate
        isQuotation
        linkedStudent {
          id
          user {
            id
            email
            firstName
            lastName
          }
        }
        linkedGroup {
          id
          name
        }
      }
    }
  }
`;

/** Change or clear the linked group, keeping the student intact. */
export const SetFakturowniaInvoiceGroupMutation = gql`
  mutation SetFakturowniaInvoiceGroup($invoiceId: ID!, $groupId: ID) {
    setFakturowniaInvoiceGroup(invoiceId: $invoiceId, groupId: $groupId) {
      success
      errors
      invoice {
        id
        groupMatchSource
        isCorrection
        isAdvance
        isFinal
        isEstimate
        isQuotation
        linkedGroup {
          id
          name
        }
      }
    }
  }
`;

/**
 * Phase 13: drop the Payment ↔ Invoice 1:1 link (and the legacy
 * `linked_*` shortcuts the backend mirrored for compatibility). Doesn't
 * delete the LMS Payment itself — the receipt stays for accounting.
 */
export const UnlinkFakturowniaInvoiceMutation = gql`
  mutation UnlinkFakturowniaInvoice($invoiceId: ID!) {
    unlinkFakturowniaInvoice(invoiceId: $invoiceId) {
      success
      errors
      invoice {
        id
        matchStatus
        payment {
          id
        }
        agreement {
          id
        }
      }
    }
  }
`;

/**
 * ТЗ 0004: pin an invoice straight to a scheduled Contribution (взнос) —
 * the new canonical linkage; no Payment is created here (the backend
 * auto-provisions the hidden Payment once the invoice is fully paid).
 * Manual re-link sets `contributionMatchSource='manual'` so the matcher
 * stops touching this invoice.
 */
export const LinkInvoiceToContributionMutation = gql`
  mutation PinInvoiceToContribution($invoiceId: ID!, $contributionId: ID!) {
    linkInvoiceToContribution(
      invoiceId: $invoiceId
      contributionId: $contributionId
    ) {
      success
      errors
      invoice {
        id
        contributionMatchSource
        provisionBlockedReason
        contribution {
          id
          date
          amountForeign
          status
          coveredAmount
          debt
          paidManually
        }
        payment {
          id
        }
      }
    }
  }
`;

/** ТЗ 0004: drop the invoice ↔ взнос link (keeps the invoice itself). */
export const UnlinkInvoiceFromContributionMutation = gql`
  mutation UnpinInvoiceFromContribution($invoiceId: ID!) {
    unlinkInvoiceFromContribution(invoiceId: $invoiceId) {
      success
      errors
      invoice {
        id
        contributionMatchSource
        provisionBlockedReason
        contribution {
          id
        }
      }
    }
  }
`;

/** Re-runs the email-based matcher for one invoice (TZ §3.3 row 5). */
export const RerunFakturowniaMatcherMutation = gql`
  mutation RerunFakturowniaMatcher($invoiceId: ID!) {
    rerunFakturowniaMatcher(invoiceId: $invoiceId) {
      success
      errors
      invoice {
        id
        matchStatus
        groupMatchSource
        isCorrection
        isAdvance
        isFinal
        isEstimate
        isQuotation
        linkedStudent {
          id
          user {
            id
            email
          }
        }
        linkedGroup {
          id
          name
        }
      }
    }
  }
`;

/** Manual expense entry — Google Drive link + categorisation (TZ §3.6). */
export const CreateExpenseMutation = gql`
  mutation CreateExpense($input: ExpenseInput!) {
    createExpense(input: $input) {
      success
      errors
      expense {
        id
        pk
        date
        amount
        amountPln
        currency
        recurrence
        recurrenceDay
        recurrenceUntil
        status
      }
    }
  }
`;

/**
 * Edit any expense. For `source=FAKTUROWNIA` the backend silently ignores
 * `date`/`amount`/`currency` and returns a warning in `errors` (TZ §3.7).
 */
export const UpdateExpenseMutation = gql`
  mutation UpdateExpense($input: UpdateExpenseInput!) {
    updateExpense(input: $input) {
      success
      errors
      expense {
        id
        pk
        amount
        amountPln
        currency
        notes
        documentUrl
        paidAt
        description
        counterpartyName
        counterpartyTaxNo
        recurrence
        recurrenceDay
        recurrenceUntil
        status
        category {
          id
          slug
          name
        }
        relatedTeacher {
          id
        }
        relatedGroup {
          id
          name
        }
        relatedFakturowniaInvoice {
          id
          pk
          number
        }
      }
    }
  }
`;

/**
 * One-click status change (planned/confirmed/paid/cancelled) — lighter than
 * UpdateExpense. The backend derives `paidAt` from the status. Returning
 * `id` + `status` + `paidAt` lets Apollo patch the cached row in place.
 */
export const SetExpenseStatusMutation = gql`
  mutation SetExpenseStatus($id: ID!, $status: String!, $paidAt: Date) {
    setExpenseStatus(id: $id, status: $status, paidAt: $paidAt) {
      success
      errors
      expense {
        id
        pk
        status
        paidAt
      }
    }
  }
`;

/** Only allowed for manual expenses — Fakturownia-sourced rows refuse. */
export const DeleteExpenseMutation = gql`
  mutation RemoveExpense($id: ID!) {
    deleteExpense(id: $id) {
      success
      errors
    }
  }
`;

/** Quick-add for the category dropdown on the expense form. */
export const CreateExpenseCategoryMutation = gql`
  mutation AddExpenseCategory($name: String!, $slug: String!) {
    createExpenseCategory(name: $name, slug: $slug) {
      success
      errors
      category {
        id
        pk
        name
        slug
        isActive
      }
    }
  }
`;

/**
 * Create a REAL VAT invoice in Fakturownia for one взнос. Unlike
 * `PinInvoiceToContribution` (which links a document that already exists),
 * this issues a fiscal document that gets a fiscal number and CANNOT be
 * deleted from the ERP.
 *
 * Operation name differs from the backend field
 * (`issueInvoiceForContribution`) — see the result-type-collapse note on the
 * preflight query.
 *
 * `errorCode` + `documentCreated` are the load-bearing failure fields:
 * `documentCreated=true` (code `CREATED_BUT_NOT_LINKED`) means a real
 * document EXISTS in Fakturownia although we are reporting failure. So does
 * `FAKTUROWNIA_AMBIGUOUS` — a transport failure after the POST, where the
 * backend returns `documentCreated=false` because it genuinely cannot tell.
 * Both must send the operator to Fakturownia (`documentMayExist` in
 * `core/utils/invoiceIssueReasons`).
 *
 * Omit `contributionId` to let the server pick the next billable взнос.
 */
export const IssueInvoiceForContributionMutation = gql`
  mutation IssueVatInvoiceForContribution(
    $agreementId: ID!
    $contributionId: ID
    $paymentTo: Date
    $allowReissue: Boolean
  ) {
    issueInvoiceForContribution(
      agreementId: $agreementId
      contributionId: $contributionId
      paymentTo: $paymentTo
      allowReissue: $allowReissue
    ) {
      success
      errors
      errorCode
      documentCreated
      invoice {
        id
        pk
        number
        status
        kind
        issueDate
        paymentTo
        currency
        priceGross
        outstanding
        viewUrl
        paymentUrl
        contribution {
          id
          date
          amountForeign
          status
          coveredAmount
          debt
        }
      }
    }
  }
`;

/**
 * BULK «Выставить фактуры» — enqueue a background run that issues a REAL
 * Fakturownia VAT document for every billable взнос of a group at once.
 * Unlike `IssueVatInvoiceForContribution` (one document, synchronous), this
 * returns immediately with a `job` the drawer polls
 * (`InvoiceIssueJobProgress`); the documents are created by a paced Celery
 * task. Each created document gets a fiscal number and CANNOT be deleted from
 * the ERP.
 *
 * Operation name differs from the backend field (`issueInvoicesForGroup`) —
 * see the result-type-collapse note on the queries.
 *
 * `agreementIds` narrows the run to those agreements (the operator's ticked
 * rows); omit it to bill everything the server finds billable.
 * `expectedBillableCount` is the count the drawer showed — the server recounts
 * and refuses with `errorCode: BILLABLE_COUNT_CHANGED` if the set moved under
 * the operator, so a stale drawer can never silently over-bill.
 * `errorCode` values: GROUP_NOT_FOUND, INTEGRATION_DISABLED,
 * BILLABLE_COUNT_CHANGED, TOO_MANY_ROWS, NO_BILLABLE_ROWS, ENQUEUE_FAILED.
 */
export const IssueInvoicesForGroupMutation = gql`
  mutation EnqueueBulkInvoiceIssue(
    $groupId: ID!
    $agreementIds: [ID!]
    $paymentTo: Date
    $expectedBillableCount: Int
  ) {
    issueInvoicesForGroup(
      groupId: $groupId
      agreementIds: $agreementIds
      paymentTo: $paymentTo
      expectedBillableCount: $expectedBillableCount
    ) {
      success
      errors
      errorCode
      total
      billableCount
      skippedCount
      job {
        id
        pk
        status
        total
        issuedCount
        skippedCount
        failedCount
      }
    }
  }
`;
